import { CornerDownRight, ExternalLink } from 'lucide-react';
import { SeveridadeBadge, StatusIncidenteBadge } from '@/components/patterns/incidente-badges';
import type { IncidenteResumo } from '@/data/types';
import { formatarDataHora, formatarRelativo, plural } from '@/lib/format';
import { cn } from '@/lib/utils';
import { rotuloDoLink } from './rotulo-link';
import { useVersoesDoIncidente } from './use-versoes-do-incidente';

const VERSOES_VISIVEIS = 3;

/**
 * Card de um incidente na lista (estilo feed): severidade e estado, título
 * (abre o detalhe), versões afetadas (3 primeiras + "+N"), ticket, quem
 * registrou e quando, e "corrigido em".
 */
export function IncidenteCard({
  incidente,
  ativo,
  aoAbrir,
}: {
  incidente: IncidenteResumo;
  ativo?: boolean;
  aoAbrir: (incidente: IncidenteResumo) => void;
}) {
  const { ref, versoes } = useVersoesDoIncidente(incidente);
  const resolvido = incidente.status === 'resolvido';
  const visiveis = versoes?.slice(0, VERSOES_VISIVEIS) ?? [];
  const resto = (versoes?.length ?? incidente.total_versoes) - visiveis.length;

  return (
    <article
      ref={ref as React.RefObject<HTMLElement>}
      aria-label={`Incidente ${incidente.id}: ${incidente.titulo}`}
      className={cn(
        'flex flex-col gap-2.5 rounded-card border bg-bg-surface p-4 shadow-sm transition-colors duration-fast ease-standard',
        ativo ? 'border-primary-border bg-primary-wash' : 'border-border hover:border-border-strong',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <SeveridadeBadge severidade={incidente.severidade} />
        <StatusIncidenteBadge status={incidente.status} />
        <span className="font-mono text-label font-normal text-on-surface-muted">#{incidente.id}</span>
        <time
          dateTime={incidente.criado_em}
          title={formatarDataHora(incidente.criado_em)}
          className="ml-auto text-cell text-on-surface-muted"
        >
          {formatarRelativo(incidente.criado_em)}
        </time>
      </div>

      <button
        type="button"
        onClick={() => aoAbrir(incidente)}
        aria-label={`Abrir o incidente ${incidente.id}: ${incidente.titulo}`}
        className={cn(
          'w-full rounded-button text-left text-card-title underline-offset-4 transition-colors duration-fast ease-standard hover:text-primary hover:underline',
          resolvido && 'text-on-surface-muted',
        )}
      >
        {incidente.titulo}
      </button>

      <div className="flex flex-wrap items-center gap-1.5" aria-label="Versões afetadas">
        {versoes === null ? (
          <span className="text-cell text-on-surface-muted">{plural(incidente.total_versoes, 'versão afetada', 'versões afetadas')}</span>
        ) : (
          <>
            {visiveis.map((versao) => (
              <span
                key={versao}
                className="rounded-chip border border-border bg-bg-subtle px-1.5 py-0.5 font-mono text-label font-normal text-on-surface"
              >
                {versao}
              </span>
            ))}
            {resto > 0 ? (
              <span
                className="text-label text-on-surface-muted"
                title={versoes.slice(VERSOES_VISIVEIS).join(', ')}
              >
                +{resto}
              </span>
            ) : null}
          </>
        )}
      </div>

      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-cell text-on-surface-muted">
        {incidente.link ? (
          <a
            href={incidente.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            <ExternalLink className="size-3.5" aria-hidden="true" />
            {rotuloDoLink(incidente.link)}
            <span className="sr-only">(ticket, abre em nova aba)</span>
          </a>
        ) : null}
        {incidente.link ? <span aria-hidden="true">·</span> : null}
        <span>
          registrado por <span className="text-on-surface">{incidente.criado_por}</span>
        </span>
        {incidente.corrigido_em ? (
          <>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1">
              <CornerDownRight className="size-3.5 text-icon" aria-hidden="true" />
              corrigido em <span className="font-mono text-on-surface">{incidente.corrigido_em}</span>
            </span>
          </>
        ) : null}
        {resolvido && incidente.resolvido_por ? (
          <>
            <span aria-hidden="true">·</span>
            <span title={formatarDataHora(incidente.resolvido_em)}>
              resolvido por {incidente.resolvido_por} {formatarRelativo(incidente.resolvido_em)}
            </span>
          </>
        ) : null}
      </p>
    </article>
  );
}
